import React from "react"

import { CATEGORY_ICONS, CATEGORY_COLORS } from "./category_styles"
import { costBreakdowns }                  from "../../helpers"

// Single-category circular badge.
const SingleBadge = ({ category, size }) => {
  const icon  = CATEGORY_ICONS[category]
  const color = CATEGORY_COLORS[category]
  if (!icon || !color) return null
  const label = I18n.t(`quotes.new.cost_category.${category}`)

  return (
    <span
      className = "cost-category-badge"
      tooltip   = {label}
      tooltip_position = "left"
      style     = {{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          `${size + 8}px`,
        height:         `${size + 8}px`,
        borderRadius:   '50%',
        background:     `${color}22`,
      }}
    >
      <i className={icon} style={{ color: color, fontSize: `${size}px` }}></i>
    </span>
  )
}

// Multi-category pie badge: a conic gradient where each slice's angle is
// proportional to that category's share of the line's purchase cost.
// Diameter matches the single badge's *icon* size (not its outer pill),
// so the pie visually weighs the same as a single-icon badge.
const MixedBadge = ({ shares, size }) => {
  const total = Object.values(shares).reduce((a, b) => a + b, 0)
  let stops   = []
  let cursor  = 0

  const entries = Object.entries(shares)
  entries.forEach(([category, amount], idx) => {
    const color   = CATEGORY_COLORS[category] || CATEGORY_COLORS.other
    const portion = total > 0 ? (amount / total) : (1 / entries.length)
    const next    = (idx === entries.length - 1) ? 360 : cursor + portion * 360
    stops.push(`${color} ${cursor}deg ${next}deg`)
    cursor = next
  })

  const tooltip = entries
    .map(([cat]) => I18n.t(`quotes.new.cost_category.${cat}`))
    .join(" / ")

  // Use the SingleBadge outer pill (size + 8) but render the pie at icon-size,
  // centered, so the visual footprint matches a single-icon badge.
  const outerDiameter = size + 8
  const pieDiameter   = size

  return (
    <span
      className = "cost-category-badge cost-category-badge--mixed"
      tooltip   = {tooltip}
      tooltip_position = "left"
      style     = {{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          `${outerDiameter}px`,
        height:         `${outerDiameter}px`,
        borderRadius:   '50%',
      }}
    >
      <span style={{
        display:      'inline-block',
        width:        `${pieDiameter}px`,
        height:       `${pieDiameter}px`,
        borderRadius: '50%',
        background:   `conic-gradient(${stops.join(', ')})`,
        boxShadow:    'inset 0 0 0 1px rgba(0,0,0,0.08)',
      }} />
    </span>
  )
}

// Public component: picks single vs mixed.
//   <CategoryBadge category="labor" />               (non-service lines)
//   <CategoryBadge breakdowns={qline.qline_breakdowns} /> (service lines)
export default function CategoryBadge({ category, breakdowns, size = 14 }) {
  if (breakdowns) {
    const valid = costBreakdowns(breakdowns).filter(b => b.cost_category)
    if (valid.length === 0) return null

    const shares = {}
    valid.forEach(b => {
      const qty    = parseFloat(b.quantity)       || 0
      const pa     = parseFloat(b.purchase_price) || 0
      const amount = qty * pa
      shares[b.cost_category] = (shares[b.cost_category] || 0) + amount
    })

    const distinctCategories = Object.keys(shares)
    if (distinctCategories.length === 1) {
      return <SingleBadge category={distinctCategories[0]} size={size} />
    }

    // If every share is 0 (e.g., prices not set yet), use equal slices.
    const allZero = Object.values(shares).every(v => v === 0)
    if (allZero) {
      distinctCategories.forEach(c => (shares[c] = 1))
    }

    return <MixedBadge shares={shares} size={size} />
  }

  if (!category) return null
  return <SingleBadge category={category} size={size} />
}
